import React from "react";
import {alpha, createTheme, styled, Theme} from "@mui/material/styles";
import Button from "@mui/material/Button";
import Tooltip, {TooltipProps, tooltipClasses} from "@mui/material/Tooltip";
import Radio from "@mui/material/Radio";
import Switch, {switchClasses} from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Checkbox from "@mui/material/Checkbox";
import {SxProps} from "@mui/material";
import {TypographyOptions} from "@mui/material/styles/createTypography";

const body1 = 15.4;

/*
 * v4's Button defaulted to `color="default"`, whose contained variant resolved its label colour with
 * getContrastText(grey[300]). v5 removed "default" entirely; its stand-in, "inherit", keeps the grey
 * background but sets `color: inherit`, so the label falls back to whatever colour it happens to
 * inherit — and neither theme installs CssBaseline, so that is not the theme's text colour. Both
 * themes pin this back onto the `containedInherit` slot.
 */
const defaultMuiPalette = createTheme().palette;
export const neutralContainedButtonTextColor = defaultMuiPalette.getContrastText(defaultMuiPalette.grey[300]);

export const MuiTypography: TypographyOptions = {
  htmlFontSize: body1,
  fontSize: body1,
  h1: {
    fontSize: 92.5,
    lineHeight: 1,
    letterSpacing: -1.5
  },
  h2: {
    fontSize: 57.8,
    lineHeight: 1.15,
    letterSpacing: -0.5
  },
  h3: {
    fontSize: 46.2,
    lineHeight: 1.25,
    letterSpacing: 0
  },
  h4: {
    fontSize: 32.7,
    lineHeight: 1.35,
    letterSpacing: 0.25
  },
  h5: {
    fontSize: 23.1,
    lineHeight: 1.45,
    letterSpacing: 0
  },
  h6: {
    fontSize: 19.2,
    lineHeight: 1.5,
    letterSpacing: 0.15
  },
  body1: {
    fontSize: body1,
    lineHeight: 1.5,
    letterSpacing: 0.5,
  },
  body2: {
    fontSize: 13.4,
    lineHeight: 1.5,
    letterSpacing: 0.25
  },
  caption: {
    fontSize: 12,
  },
  overline: {
    fontSize: 10
  },
  fontWeightBold: 600,
  fontFamily: [
    'Poppins',
    'system-ui',
    'sans-serif',
  ].join(','),
  button: {
    textTransform: "none",
  }
};

export const CallToActionButton = styled(Button)({
  paddingTop: "0.65rem",
  paddingBottom: "0.65rem",
  paddingLeft: "1.25rem",
  paddingRight: "1.25rem",
  fontSize: "1.11rem",
  fontWeight: 600,
});

export const CloseTooltipStyling = {
  [`& .${tooltipClasses.tooltipPlacementTop}`]: {
    margin: "6px 0",
  },
  [`& .${tooltipClasses.tooltipPlacementBottom}`]: {
    margin: "10px 0",
  },
  [`& .${tooltipClasses.tooltipPlacementRight}`]: {
    margin: "0 6px",
  },
  [`& .${tooltipClasses.tooltipPlacementLeft}`]: {
    margin: "0 6px",
  },
}

// v5 renders the tooltip in a portal, so the placement slots are only reachable by styling the
// popper. Routing the generated class through classes.popper is the supported way to do that.
const PopperStyledTooltip = React.forwardRef<HTMLElement, TooltipProps>(
  ({className, ...props}, ref) => <Tooltip {...props} ref={ref} classes={{popper: className}}/>
);

export const CloseTooltip = styled(PopperStyledTooltip)({
  ...CloseTooltipStyling,
});

export const CloseBottomTooltip = styled(PopperStyledTooltip)({
  [`& .${tooltipClasses.tooltipPlacementBottom}`]: {
    margin: "10px 0",
  },
});

export function buttonWithClr(bgClr: string, textClr: string): any {
  return styled(Button)({
    color: textClr,
    backgroundColor: bgClr,
    '&:hover': {
      borderColor: bgClr,
      backgroundColor: alpha(bgClr, 0.85),
    },
  });
}

export function radioWithClr(passedInColor: string): any {
  return styled(Radio)({
    color: passedInColor,
    '&.Mui-checked': {
      color: passedInColor,
    },
  });
}

export function switchWithClr(passedInColor: string): any {
  return styled(Switch)({
    [`& .${switchClasses.switchBase}.${switchClasses.checked}`]: {
      color: passedInColor,
    },
    [`& .${switchClasses.switchBase}.${switchClasses.checked} + .${switchClasses.track}`]: {
      backgroundColor: passedInColor,
    },
    [`& .${switchClasses.track}`]: {
      backgroundColor: passedInColor,
    },
  });
}

export function textFieldWithClr(passedInColor: string): any {
  return styled(TextField)({
    '& label.Mui-focused': {
      color: passedInColor,
    },
    '& .MuiInput-underline:not(.Mui-error):after': {
      borderBottomColor: passedInColor,
    },
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: passedInColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: passedInColor,
      },
    },
  });
}

export function formControlWithClr(passedInColor: string): any {
  return styled(FormControl)({
    '& label.Mui-focused': {
      color: passedInColor,
    },
    '& .MuiInput-underline:not(.Mui-error):after': {
      borderBottomColor: passedInColor,
    },
    '& .MuiInputLabel-outlined[data-shrink="false"]': {
      display: "none",
    },
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: passedInColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: passedInColor,
      },
    },
  });
}

export function checkboxWithClr(passedInColor: string): any {
  return styled(Checkbox)({
    color: passedInColor,
    '&.Mui-checked': {
      color: passedInColor,
    },
  });
}

export const radioSx: Record<"smallRadio" | "mediumRadio" | "growLabel", SxProps<Theme>> = {
  smallRadio: {
    "& .MuiSvgIcon-root": {
      fontSize: "1rem",
    },
    padding: "4px",
  },
  mediumRadio: {
    "& .MuiSvgIcon-root": {
      fontSize: "1.35rem",
    },
    padding: "6px",
  },
  growLabel: {
    "& .MuiFormControlLabel-label": {
      flexGrow: 1,
    },
  }
};

export const accountIconSx: Record<"button" | "popper", SxProps<Theme>> = {
  button: {
    minWidth: "30px",
  },
  popper: {
    zIndex: 1101,
  }
};
